import { useEffect, useMemo } from 'react';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useClientStore } from '@core/client/useClientStore';
import { useUserStore } from '@core/user/useUserStore';
import { useTenantId } from '@/contexts/TenantContext';
import type { JobCreationView } from '@/modules/jobs/views/jobCreation.view';

export const useJobCreateData = (
  view: JobCreationView,
  clientId: string | null | undefined,
) => {
  const tenantId = useTenantId();
  const { currentUser } = useUserStore();
  const currentUserClientId = currentUser?.clientId ?? undefined;
  const { clients, fetchClientsForTenant, fetchClientById, getClientById } =
    useClientStore();
  const {
    fetchClassificationGroups,
    getClassificationGroupsByClassifications,
    isLoading: isClassificationLoading,
  } = useClassificationStore();

  useEffect(() => {
    if (!tenantId) return;
    if (!view.query.classifications.fetch) return;

    void fetchClassificationGroups(tenantId);
  }, [tenantId, view.query.classifications.fetch, fetchClassificationGroups]);

  useEffect(() => {
    if (!tenantId) return;

    switch (view.query.clients.strategy) {
      case 'tenantAll':
        void fetchClientsForTenant(tenantId, undefined, undefined, true);
        break;
      case 'currentUser':
        if (currentUserClientId && !getClientById(currentUserClientId)) {
          void fetchClientById(currentUserClientId);
        }
        break;
      default:
        break;
    }
  }, [
    tenantId,
    view.query.clients.strategy,
    currentUserClientId,
    fetchClientsForTenant,
    fetchClientById,
    getClientById,
  ]);

  const classificationGroups = useMemo(() => {
    if (!clientId) return [];

    const client = clients.find((c) => c.id === clientId);
    if (!client) return [];

    return getClassificationGroupsByClassifications(
      client.classifications || [],
    );
  }, [clientId, clients, getClassificationGroupsByClassifications]);

  return {
    clients,
    classificationGroups,
    isClassificationLoading,
    currentUserClientId,
  };
};
